import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Form } from 'react-form';
import PropTypes from 'prop-types';
import { Input, Select, PlayerSearch, CheckBox } from '../../Form';
import { required } from '../../../utils/validation';
import Popup from '../../Popup';
import { getPlayerName } from '../../../utils';
import styles from './registerPlayerToEventPopUp.scss';
import { addPlayerByOrganizerV2, lookupRegistrations } from '../../../actions/event-profile-actions';
import { isDinglesOrDoublesFormat, isSinglesFormat, isDinglesFormat } from '../../../utils/events';
import { divisionFormatTypeIds, eventGenderTypeIds, freeDivision, sportTypeIds } from '../../../utils/eventConstants';
import { useStatsig } from '../../hooks/useStatsig';
import { getConflictMessages } from './conflictDetection';

const LOOKUP_DEBOUNCE_MS = 250;

const genderLabels = { Male: 2, Female: 3 };

const RegisterPlayerToEventPopUpV2 = (props) => {
    const { onCloseClick, divisions, isDemoEvent, eventId, onPlayerAdded, sportTypeId } = props;
    const [submitting, setSubmitting] = useState(false);
    const [playerName, setPlayerName] = useState();
    const [playerTextName, setPlayerTextName] = useState();
    const [partnerPlayerName, setPartnerPlayerName] = useState();
    const [partnerPlayerTextName, setPartnerPlayerTextName] = useState();
    const [playerGenderId, setPlayerGenderId] = useState();
    const [partnerPlayerGenderId, setPartnerPlayerGenderId] = useState();
    const [success, setSuccess] = useState(false);
    const [divisionFormatTypeId, setDivisionFormatTypeId] = useState();
    const [submittedDivisionId, setSubmittedDivisionId] = useState(null);
    const [selectedMemberId, setSelectedMemberId] = useState(null);
    const [selectedPartnerMemberId, setSelectedPartnerMemberId] = useState(null);
    const [selectedDivisionId, setSelectedDivisionId] = useState(null);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupResults, setLookupResults] = useState({});
    const [lookupError, setLookupError] = useState(false);
    const [conflictErrors, setConflictErrors] = useState({});
    const formApiRef = useRef(null);
    const windowInnerHeight = window?.innerHeight > 650 ? 650 : window.innerHeight;
    const minHeight = window ? windowInnerHeight : '100%';

    const dispatch = useDispatch();

    const submitData = async (values) => {
        const res = await dispatch(addPlayerByOrganizerV2(eventId, values)); // NOSONAR — Redux thunk returns a promise
        if (res) {
            setSubmitting(false);
            setSubmittedDivisionId(values.divisionId);
            setSuccess(true);
        }
    };

    const defaultValues = {
        playerId: null,
        partnerPlayerId: null,
        divisionId: null,
    };

    const { checkGate, featureGates } = useStatsig();
    const isDinglesEnabled = checkGate(featureGates.DINGLES);
    const paymentRequiredEnabled = checkGate(featureGates.ADD_PLAYER_PAYMENT_OWED);

    const selectedPlayerMatchesTextName = playerName === playerTextName;
    const selectedPartnetPlayerMatchesTextName = partnerPlayerName === partnerPlayerTextName;
    const selectedPlayersMatchTextNames = !!selectedPlayerMatchesTextName && !!selectedPartnetPlayerMatchesTextName;
    const isFreeEvent = divisions.every(division => division.price === freeDivision);

    useEffect(() => {
        if (!selectedMemberId) {
            setLookupResults({});
            return undefined;
        }
        if (isDinglesOrDoublesFormat(divisionFormatTypeId) && !selectedPartnerMemberId) {
            setLookupResults({});
            return undefined;
        }

        setLookupResults({});
        setLookupError(false);
        setLookupLoading(true);

        const memberIds = [parseInt(selectedMemberId, 10)];
        if (selectedPartnerMemberId) {
            memberIds.push(parseInt(selectedPartnerMemberId, 10));
        }

        const controller = new AbortController();
        const timerId = setTimeout(async () => {
            try {
                const data = await dispatch(lookupRegistrations(eventId, memberIds, controller.signal)); // NOSONAR — Redux thunk returns a promise
                setLookupResults(data.registrations.reduce((acc, r) => {
                    acc[r.memberId] = r;
                    return acc;
                }, {}));
            } catch {
                setLookupError(true);
            } finally {
                setLookupLoading(false);
            }
        }, LOOKUP_DEBOUNCE_MS);

        return () => {
            clearTimeout(timerId);
            controller.abort();
        };
    }, [selectedMemberId, selectedPartnerMemberId, divisionFormatTypeId, eventId, dispatch]);

    useEffect(() => {
        if (!formApiRef.current) {
            return;
        }

        formApiRef.current.setError('playerId', null);
        formApiRef.current.setError('partnerPlayerId', null);

        if (lookupError) {
            formApiRef.current.setError('playerId', 'Partner verification failed. Please try again.');
            setConflictErrors({ playerId: 'Partner verification failed. Please try again.' });
            return;
        }

        if (!selectedMemberId || !selectedDivisionId || Object.keys(lookupResults).length === 0) {
            setConflictErrors({});
            return;
        }

        if (!formApiRef.current) {
            return;
        }

        formApiRef.current.setError('divisionId', null);

        const messages = getConflictMessages({
            lookupResults,
            selectedMemberId,
            selectedPartnerMemberId,
            selectedDivisionId,
            selectedPlayerName: playerName,
            selectedPartnerPlayerName: partnerPlayerName,
        });

        const nextConflictErrors = {};
        ['playerId', 'partnerPlayerId', 'divisionId'].forEach((field) => {
            if (messages[field]) {
                formApiRef.current.setError(field, messages[field]);
                nextConflictErrors[field] = messages[field];
            }
        });

        setConflictErrors(nextConflictErrors);
    }, [selectedMemberId, selectedPartnerMemberId, selectedDivisionId, lookupResults, lookupError, playerName, partnerPlayerName]);

    const validatePlayerId = useCallback(
        val => required(val) || conflictErrors.playerId,
        [conflictErrors.playerId],
    );

    const validatePartnerPlayerId = useCallback(
        () => conflictErrors.partnerPlayerId,
        [conflictErrors.partnerPlayerId],
    );

    const showDivision = (values) => {
        return !!values.playerId;
    };

    const handleDivisionFormatType = (val, values, setValue, setError, getError) => {
        setDivisionFormatTypeId(val);
        const getDivisionError = getError('divisionId');
        if (getDivisionError) {
            setError('divisionId', null);
        }
        if (values.divisionId) {
            setValue('divisionId', null);
        }
        if (isSinglesFormat(parseInt(val, 10)) && values.partnerPlayerId) {
            setValue('partnerPlayerId', null);
        }
    };

    const validateDivisionChange = (val, values, divisionFormatTypeId, divisionOptions) => {
        const isDivisionAcceptanceMatched = (isDinglesOrDoublesFormat(divisionFormatTypeId) && !!values.partnerPlayerId) ? (!!values.playerId && !!values.partnerPlayerId) : !!values.playerId;
        if (isDivisionAcceptanceMatched && divisionOptions.length === 0) {
            setSelectedDivisionId(null);
            return {
                error: 'Player does not meet division acceptance criteria',
            };
        }
        if (!val) {
            setSelectedDivisionId(null);
            return {
                error: 'This field is required.',
            };
        }
        setSelectedDivisionId(val);
        if (conflictErrors.divisionId) {
            return { error: conflictErrors.divisionId };
        }
    };

    const getSuccessMessage = (divisionFormatTypeId, playerName, partnerPlayerName) => {
        if (isDinglesOrDoublesFormat(divisionFormatTypeId) && playerName && partnerPlayerName) {
            return `Successfully added ${playerName} and ${partnerPlayerName} to event`;
        }
        if (playerName) {
            return `Successfully added ${playerName} to event`;
        }
        return `Successfully added player to event`;
    };

    const getSubmitValue = (submitting) => {
        return `${submitting ? 'Adding' : 'Add'} Player(s)`;
    };

    const getDivisionOptions = (divisions, divisionFormatTypeId, playerGenderId, partnerPlayerGenderId) => {
        const divisionOptions = [];
        divisions.forEach((div) => {
            const isEligibleDivision = parseInt(div.divisionFormatTypeId, 10) === divisionFormatTypeId;
            const isPlayerGenderMatched = div.genderId === playerGenderId || div.genderId === eventGenderTypeIds.ALL;
            const isPartnerPlayerGenderMatched = div.genderId === partnerPlayerGenderId || div.genderId === eventGenderTypeIds.ALL;
            const isMixedDivisionGenderTypeMatched = checkIsMixedDivisionGenderTypeMatched(playerGenderId, partnerPlayerGenderId);
            const isDivisionGenderMatched = checkIsDivisionGenderMatched(divisionFormatTypeId, partnerPlayerGenderId, isPlayerGenderMatched, isPartnerPlayerGenderMatched);
            if (isEligibleDivision && (isDivisionGenderMatched || (div.genderId === eventGenderTypeIds.MIXED && isMixedDivisionGenderTypeMatched)) && !div.isEventEntryFeeDivision) {
                divisionOptions.push({
                    id: div.id,
                    label: div.name,
                    value: div.name,
                });
            }
        });
        return divisionOptions;
    };

    const checkIsDivisionGenderMatched = (divisionFormatTypeId, partnerPlayerGenderId, isPlayerGenderMatched, isPartnerPlayerGenderMatched) => {
        if (isDinglesOrDoublesFormat(divisionFormatTypeId) && partnerPlayerGenderId) {
            return isPlayerGenderMatched && isPartnerPlayerGenderMatched;
        }
        return isPlayerGenderMatched;
    };

    const checkIsMixedDivisionGenderTypeMatched = (playerGenderId, partnerPlayerGenderId) => {
        if (partnerPlayerGenderId) {
            return (playerGenderId === eventGenderTypeIds.MALE && partnerPlayerGenderId === eventGenderTypeIds.FEMALE) || (playerGenderId === eventGenderTypeIds.FEMALE && partnerPlayerGenderId === eventGenderTypeIds.MALE);
        }
        return !!playerGenderId;
    };

    const getPlayerSearchTitle = () => {
        if (isDinglesOrDoublesFormat(divisionFormatTypeId)) {
            return 'Select players from members';
        }
        return 'Select a player from members';
    };

    const showDingles = (isDinglesEnabled, divisions) => {
        const isDinglesDivision = divisions?.find(div => isDinglesFormat(parseInt(div?.divisionFormatTypeId, 10)));
        return isDinglesEnabled && !!isDinglesDivision;
    };

    let divisionHeight;
    if (divisionFormatTypeId === 1) { divisionHeight = 670; } else if (divisionFormatTypeId === 2) { divisionHeight = 745; }

    return (
        <Popup
            className={`popup-md`}
            popUpClassName={`${styles.addUpdatePlayerOnRosterPopUp}`}
            popUpContentWrapperClassName={`overflow-unset`}
            onCloseClick={onCloseClick}
            config={{
                noPadding: true,
                fullScreen: false,
                topGradient: false,
                scrollable: !!(!success && divisionFormatTypeId),
                showCloseIcon: false,
                style: (!success && divisionFormatTypeId) ? {
                    height: divisionHeight,
                    width: 500,
                    minHeight,
                    maxHeight: '100%',
                } : { width: 500 },
            }}
        >
            {!success && (
                <>
                    <div className={`${styles.section} pt0 pb16`}>
                        <h4 data-testid="modal.register-player-v2.title">
                            Add player(s)
                        </h4>
                    </div>
                    <div className={`${styles.section} pt0 pb24`}>
                        <Form
                            getApi={api => { formApiRef.current = api; }}
                            onSubmit={(values) => {
                                setSubmitting(true);
                                submitData(values);
                            }}
                            id="registerPlayerForm"
                            defaultValues={defaultValues}
                        >
                            {({ submitForm, setValue, values, errors, setError, getError }) => {
                                const divisionOptions = getDivisionOptions(divisions, divisionFormatTypeId, playerGenderId, partnerPlayerGenderId);
                                return (
                                    <form onSubmit={submitForm} id="registerPlayerByOrganizerForm" data-testid="modal.register-player-v2.form">
                                        <div className="row mb16">
                                            <div className="col-12">
                                                <button
                                                    type="button"
                                                    className={`${showDingles(isDinglesEnabled, divisions) ? 'col-md-4' : 'col-md-6'} text-uppercase ${styles.button} ${isSinglesFormat(divisionFormatTypeId) ? styles.buttonSelected : ''}`}
                                                    onClick={() => handleDivisionFormatType(divisionFormatTypeIds.SINGLES, values, setValue, setError, getError)}
                                                    data-testid="modal.register-player-v2.singles-btn"
                                                >
                                                    Singles
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`${showDingles(isDinglesEnabled, divisions) ? 'col-md-4' : 'col-md-6'} text-uppercase ${styles.button} ${divisionFormatTypeId === divisionFormatTypeIds.DOUBLES ? styles.buttonSelected : ''}`}
                                                    onClick={() => handleDivisionFormatType(divisionFormatTypeIds.DOUBLES, values, setValue, setError, getError)}
                                                    data-testid="modal.register-player-v2.doubles-btn"
                                                >
                                                    Doubles
                                                </button>
                                                {showDingles(isDinglesEnabled, divisions) && (
                                                    <button
                                                        type="button"
                                                        className={`col-md-4 text-uppercase ${styles.button} ${isDinglesFormat(divisionFormatTypeId) ? styles.buttonSelected : ''}`}
                                                        onClick={() => handleDivisionFormatType(divisionFormatTypeIds.DINGLES, values, setValue, setError, getError)}
                                                        data-testid="modal.register-player-v2.dingles-btn"
                                                    >
                                                        Dingles
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {divisionFormatTypeId && (
                                            <>
                                                <div className={`form-group form-group-xl description`}>
                                                    {paymentRequiredEnabled && !isFreeEvent && (
                                                        <>
                                                            <label data-testid="pop-ups.register-player-to-event-pop-up.label-0-label">{parseInt(divisionFormatTypeId, 10) === divisionFormatTypeIds.SINGLES ? 'Payment' : 'Team payment'}</label>
                                                            <br />
                                                            <span className="text-black fs14 fw325" data-testid="modal.register-player-v2.payment-description">
                                                                If enabled, added player(s) will be prompted to complete payment upon visiting the event profile page and will be listed as &apos;Pending&apos; until payment is made.
                                                            </span>
                                                            <CheckBox
                                                                id="paymentRequired"
                                                                customLabel="Require payment"
                                                                field="paymentRequired"
                                                                formGroupCssClass="mt8 mb8"
                                                            />
                                                        </>
                                                    )}
                                                    <label htmlFor={`playerId`} data-testid="pop-ups.register-player-to-event-pop-up.`playerid`-label">{parseInt(divisionFormatTypeId, 10) === divisionFormatTypeIds.SINGLES ? 'Player' : 'Players'}</label>
                                                    <small>
                                                        {getPlayerSearchTitle()}
                                                    </small>
                                                    <Input
                                                        id="playerId"
                                                        field={'playerId'}
                                                        type="hidden"
                                                        validate={validatePlayerId}
                                                        disableProfanityCheck={true}
                                                    />
                                                    <div className="row">
                                                        <div className="col-12">
                                                            <PlayerSearch
                                                                className="form-control"
                                                                placeholder="Search players"
                                                                showInvitePlayer={false}
                                                                showDemoPlayers={isDemoEvent}
                                                                handlePlayerSelect={(player) => {
                                                                    setValue('playerId', player.id);
                                                                    setSelectedMemberId(player.memberId);
                                                                    setPlayerName(getPlayerName(player.firstName, player.lastName));
                                                                    setPlayerTextName(getPlayerName(player.firstName, player.lastName));
                                                                    setPlayerGenderId(genderLabels[player.gender]);
                                                                    const getDivisionError = getError('divisionId');
                                                                    if (player.id !== values.playerId && getDivisionError) {
                                                                        setError('divisionId', null);
                                                                    }
                                                                }}
                                                                searchMembersOnly={true}
                                                                excludeProPlayers={true}
                                                                closeAfterSelect={true}
                                                                sportTypeId={sportTypeId}
                                                                handlePlayerNameTextChange={(playerTextName) => {
                                                                    setPlayerTextName(playerTextName === '' ? null : playerTextName);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    {isDinglesOrDoublesFormat(parseInt(divisionFormatTypeId, 10)) && (
                                                        <>
                                                            <Input
                                                                id="partnerPlayerId"
                                                                field={'partnerPlayerId'}
                                                                type="hidden"
                                                                validate={validatePartnerPlayerId}
                                                            />
                                                            <div className="row mb32">
                                                                <div className="col-12">
                                                                    <PlayerSearch
                                                                        className="form-control"
                                                                        placeholder="Search players"
                                                                        showInvitePlayer={false}
                                                                        showDemoPlayers={isDemoEvent}
                                                                        handlePlayerSelect={(player) => {
                                                                            setValue('partnerPlayerId', player.id);
                                                                            setSelectedPartnerMemberId(player.memberId);
                                                                            setPartnerPlayerName(getPlayerName(player.firstName, player.lastName));
                                                                            setPartnerPlayerTextName(getPlayerName(player.firstName, player.lastName));
                                                                            setPartnerPlayerGenderId(genderLabels[player.gender]);
                                                                            const getDivisionError = getError('divisionId');
                                                                            if (player.id !== values.partnerPlayerId && getDivisionError) {
                                                                                setError('divisionId', null);
                                                                            }
                                                                        }}
                                                                        searchMembersOnly={true}
                                                                        excludeProPlayers={true}
                                                                        closeAfterSelect={true}
                                                                        sportTypeId={sportTypeId}
                                                                        handlePlayerNameTextChange={(playerTextName) => {
                                                                            setPartnerPlayerTextName(playerTextName === '' ? null : playerTextName);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                                {showDivision(values) && (
                                                    <div className="row mb32">
                                                        <div className="col-12">
                                                            <Select
                                                                key={`divisionId`}
                                                                id={`divisionId`}
                                                                label="Division"
                                                                description="Select the primary division for registration. You can add/move player(s) to any singles or doubles draws after registration."
                                                                field={'divisionId'}
                                                                options={divisionOptions}
                                                                validate={val => validateDivisionChange(val, values, divisionFormatTypeId, divisionOptions)}
                                                                emptyOption="Select division..."
                                                                data-testid="registerplayertoeventpopupv2.division-id-select"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        <div className="row">
                                            <div className="col-12 col-md-6 mb16">
                                                <button
                                                    onClick={onCloseClick}
                                                    type="button"
                                                    className="btn btn-default btn-xl text-uppercase btn-block"
                                                    disabled={submitting}
                                                    data-testid="modal.register-player-v2.cancel-btn"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary btn-xl text-uppercase btn-block"
                                                    disabled={!values.divisionId || submitting || lookupLoading || errors || !selectedPlayersMatchTextNames}
                                                    data-testid="modal.register-player-v2.submit-btn"
                                                >
                                                    {(submitting || lookupLoading) && (<i className="fas fa-circle-notch fa-spin mr8" />)}
                                                    {getSubmitValue(submitting)}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                );
                            }}
                        </Form>
                        <div className={`${styles.section} pt0 pb0`}><div /></div>
                    </div>
                </>
            )}
            {success && (
                <>
                    <div className={`${styles.section} pt0 pb24`}>
                        <h6 data-testid="modal.register-player-v2.success-msg">
                            {getSuccessMessage(divisionFormatTypeId, playerName, partnerPlayerName)}
                        </h6>
                    </div>
                    <div className={`${styles.section} pt0 pb24 pt24`}>
                        <div className="row">
                            <div className="col-12 offset-md-3 col-md-6">
                                <button
                                    className="btn btn-primary btn-xl text-uppercase btn-block"
                                    onClick={() => onPlayerAdded(submittedDivisionId)}
                                    type="button"
                                    data-testid="modal.register-player-v2.ok-btn"
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </Popup>
    );
};

RegisterPlayerToEventPopUpV2.propTypes = {
    onCloseClick: PropTypes.func.isRequired,
    divisions: PropTypes.instanceOf(Array).isRequired,
    isDemoEvent: PropTypes.bool,
    eventId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    onPlayerAdded: PropTypes.func.isRequired,
    sportTypeId: PropTypes.number,
};

RegisterPlayerToEventPopUpV2.defaultProps = {
    isDemoEvent: false,
    sportTypeId: sportTypeIds.TENNIS,
};

export default RegisterPlayerToEventPopUpV2;
